import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Button, useToast } from '@kss/ui';
import {
  Plus, ChevronDown, Sheet, RefreshCw,
  Upload, Download, FileDown,
} from 'lucide-react';
import { useModulePerms } from '../../hooks/useModulePerms';

export interface AddDropdownItem {
  label: string;
  /** Navigation target. Ignored if `onClick` is provided. */
  to?: string;
  /** Action handler. If set, runs instead of navigation. */
  onClick?: () => void;
  icon?: ReactNode;
  /** Render a thin separator line ABOVE this item. */
  divider?: boolean;
  /** Disable the item (e.g. while an import is in flight). */
  disabled?: boolean;
}

export interface AddDropdownProps {
  /** Visible button label. Defaults to "Add". */
  label?: string;
  /** Menu entries. */
  items: AddDropdownItem[];
}

/**
 * Reusable "Add ▾" dropdown used on every list page toolbar. Opens a small
 * popover via portal so the scroll parent doesn't clip it.
 *
 * Outside-click handling checks both the trigger AND the portal menu so
 * mousedown on a menu item doesn't close the dropdown before the click
 * handler runs.
 */
export function AddDropdown({ label = 'Add', items }: AddDropdownProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (btnRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    document.addEventListener('mousedown', onDoc);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [open]);

  const handleItem = (it: AddDropdownItem) => {
    if (it.disabled) return;
    setOpen(false);
    if (it.onClick) it.onClick();
    else if (it.to) navigate(it.to);
  };

  return (
    <>
      <Button ref={btnRef} size="sm" onClick={() => setOpen((o) => !o)}>
        <Plus className="h-3.5 w-3.5" /> {label} <ChevronDown className="h-3 w-3" />
      </Button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: 9999 }}
            className="w-64 rounded-md border border-border bg-surface-card py-1 shadow-xl"
          >
            {items.map((it, i) => (
              <div key={i}>
                {it.divider && <div className="my-1 border-t border-border" />}
                <button
                  type="button"
                  disabled={it.disabled}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-content-secondary hover:bg-surface-card-hover disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => handleItem(it)}
                >
                  {it.icon ?? <Plus className="h-3.5 w-3.5" />}
                  <span>{it.label}</span>
                </button>
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}

/**
 * Minimal contract a service must satisfy to power Export.
 */
export interface ExportableService {
  all: () => Promise<unknown>;
}

/**
 * Convenience: pass an entity base path and this builds the standard list-page
 * dropdown {New, Bulk import (spreadsheet), Import CSV, Export CSV, Download
 * template}.
 *
 * - **Import CSV** routes to the existing bulk page (same as Bulk import).
 * - **Export CSV** is fully client-side: it calls `service.all()` and converts
 *   the JSON to a CSV download. No backend export endpoint required.
 * - **Download template** writes a header-only CSV using `templateFields`,
 *   so an admin can fill in rows offline and import them via the bulk page.
 *
 * If `service` or `templateFields` is omitted, those rows are not rendered.
 */
export function StandardAddDropdown({
  basePath,
  newLabel,
  service,
  templateFields,
  exportFilename,
  module,
}: {
  basePath: string;
  newLabel: string;
  /** Optional `@kss/api` service whose `all()` will feed CSV export. */
  service?: ExportableService;
  /**
   * Header columns for the downloadable template CSV. Order is preserved.
   * When omitted, "Download template" is hidden.
   */
  templateFields?: string[];
  /** Override download filename stem. Default is the last segment of basePath. */
  exportFilename?: string;
  /**
   * Backend module name (e.g. "Product", "SalesOrder") used to gate items
   * by JWT permissions. When omitted, derived from basePath. Pass it
   * explicitly when the URL slug doesn't match the backend module.
   */
  module?: string;
}) {
  const toast = useToast();
  const stem = exportFilename ?? basePath.replace(/\/$/, '').split('/').pop() ?? 'export';
  const moduleName = module ?? slugToModule(stem);
  const perms = useModulePerms(moduleName);

  const handleExport = async () => {
    if (!service) return;
    try {
      const data = await service.all();
      if (!Array.isArray(data) || data.length === 0) {
        toast.info('Nothing to export', 'The list is empty.');
        return;
      }
      downloadCsv(`${stem}-${todayStamp()}.csv`, jsonRowsToCsv(data as Record<string, unknown>[]));
      toast.success('Export ready', `${data.length} row(s) exported.`);
    } catch (e) {
      toast.error('Export failed', e instanceof Error ? e.message : 'Could not export.');
    }
  };

  const handleTemplate = () => {
    if (!templateFields || templateFields.length === 0) return;
    downloadCsv(`${stem}-template.csv`, templateFields.join(',') + '\n');
    toast.success('Template downloaded', `${templateFields.length} column(s).`);
  };

  const items: AddDropdownItem[] = [];
  if (perms.canCreate) {
    items.push({ label: newLabel, to: `${basePath}/new`, icon: <Plus className="h-3.5 w-3.5" /> });
  }
  if (perms.canImport) {
    items.push({ label: 'Bulk import (spreadsheet)', to: `${basePath}/bulk`, icon: <Sheet className="h-3.5 w-3.5" /> });
    items.push({ label: 'Import CSV', to: `${basePath}/bulk`, icon: <Upload className="h-3.5 w-3.5" />, divider: true });
  }
  if (service && perms.canExport) {
    items.push({
      label: 'Export CSV',
      onClick: handleExport,
      icon: <Download className="h-3.5 w-3.5" />,
    });
  }
  if (templateFields && templateFields.length > 0 && perms.canDownloadTemplate) {
    items.push({
      label: 'Download template',
      onClick: handleTemplate,
      icon: <FileDown className="h-3.5 w-3.5" />,
    });
  }

  // Hide the entire dropdown when the user has no perms in this module —
  // a "+ Add" with an empty popover is just visual noise.
  if (items.length === 0) return null;
  return <AddDropdown items={items} />;
}

/**
 * Convert "/panel/sales-orders" or "sales-orders" → "SalesOrder" so we
 * can probe the JWT for `SalesOrder.{Action}` perms. Best-effort: pass
 * the explicit `module` prop when the slug doesn't follow the convention
 * (e.g. "tenant-domain-maps" → "TenantDomainMap").
 */
function slugToModule(slug: string): string {
  const parts = slug.split(/[-_]/).filter(Boolean);
  // Drop trailing plural "s" so "products" → "Product".
  const last = parts[parts.length - 1] ?? '';
  if (parts.length > 0 && last.endsWith('s') && last.length > 2) {
    parts[parts.length - 1] = last.slice(0, -1);
  }
  return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join('');
}

/* ------------------------------------------------------------------------ */
/* CSV helpers — kept private to this module so consumers don't need to     */
/* think about escaping.                                                     */
/* ------------------------------------------------------------------------ */

function jsonRowsToCsv(rows: Record<string, unknown>[]): string {
  // Union of every key seen across rows, preserving first-seen order.
  const seen = new Set<string>();
  const headers: string[] = [];
  for (const r of rows) {
    for (const k of Object.keys(r)) {
      if (!seen.has(k)) {
        seen.add(k);
        headers.push(k);
      }
    }
  }
  const lines: string[] = [headers.join(',')];
  for (const r of rows) {
    lines.push(headers.map((h) => csvCell(r[h])).join(','));
  }
  return lines.join('\n');
}

function csvCell(v: unknown): string {
  if (v == null) return '';
  let s: string;
  if (typeof v === 'object') {
    try {
      s = JSON.stringify(v);
    } catch {
      s = String(v);
    }
  } else {
    s = String(v);
  }
  // Quote if the value contains anything that would corrupt a CSV row.
  if (/[",\n\r]/.test(s)) {
    s = '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

function downloadCsv(filename: string, body: string): void {
  const blob = new Blob(['﻿' + body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Allow the browser to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function todayStamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

export function RefreshButton({ onClick, loading }: { onClick: () => void; loading?: boolean }) {
  return (
    <Button variant="outline" size="sm" onClick={onClick} disabled={loading}>
      <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
    </Button>
  );
}
